import {DebouncedFunction} from 'ts-debounce';

import {BulkAssemblyProcedure} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {AsyncBulkAssemblyValidationResults} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {NEW_VALIDATION_REQUEST_ERROR_MESSAGE} from 'molbio/bulk-assembly/useAssemblyCoordinator';
import notifications, {GenericResponseJSON} from 'notifications';
import {getStore} from 'redux-stores/Store';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import * as net from 'util/Net';

export enum BulkAssemblyActionType {
  BULK_ASSEMBLY_INITIALIZED = 'BULK_ASSEMBLY_INITIALIZED',
  BULK_ASSEMBLY_STATE_UPDATED = 'BULK_ASSEMBLY_STATE_UPDATED',
  BULK_ASSEMBLY_VALIDATION_STARTED = 'BULK_ASSEMBLY_VALIDATION_STARTED',
  BULK_ASSEMBLY_VALIDATION_FAILED = 'BULK_ASSEMBLY_VALIDATION_FAILED',
  BULK_ASSEMBLY_VALIDATION_COMPLETED = 'BULK_ASSEMBLY_VALIDATION_COMPLETED',
  BULK_ASSEMBLY_FRAGMENT_STATUS_FILTER_UPDATED = 'BULK_ASSEMBLY_FRAGMENT_STATUS_FILTER_UPDATED',
  BULK_ASSEMBLY_CONSTRUCT_STATUS_FILTER_UPDATED = 'BULK_ASSEMBLY_CONSTRUCT_STATUS_FILTER_UPDATED',
}

interface BulkAssemblyInitializedAction {
  type: BulkAssemblyActionType.BULK_ASSEMBLY_INITIALIZED;
  assemblyId: string;
  procedure: BulkAssemblyProcedure;
}

export function bulkAssemblyInitialized(
  assemblyId: string,
  procedure: BulkAssemblyProcedure
): BulkAssemblyInitializedAction {
  return {type: BulkAssemblyActionType.BULK_ASSEMBLY_INITIALIZED, assemblyId, procedure};
}

export async function startBulkAssemblyAsyncValidation(
  assemblyId: string,
  procedureJson: BulkAssemblyProcedure,
  debouncedValidate: DebouncedFunction<
    (
      bulkAssemblyId: string,
      procedureJson: BulkAssemblyProcedure
    ) => Promise<AsyncBulkAssemblyValidationResults>
  >
): Promise<void> {
  if (getStore().getState().ui.molbio.bulkAssemblies[assemblyId]?.asyncState.status === AsyncStatus.LOADING) {
    debouncedValidate.cancel(NEW_VALIDATION_REQUEST_ERROR_MESSAGE);
  }
  getStore().dispatch({
    type: BulkAssemblyActionType.BULK_ASSEMBLY_VALIDATION_STARTED,
    assemblyId,
    input: procedureJson,
  });
  try {
    const validationPromise = debouncedValidate(assemblyId, procedureJson);
    const newData = await validationPromise;
    getStore().dispatch({
      type: BulkAssemblyActionType.BULK_ASSEMBLY_VALIDATION_COMPLETED,
      assemblyId,
      data: newData,
    });
  } catch (error) {
    if (error === NEW_VALIDATION_REQUEST_ERROR_MESSAGE) {
      return;
    }
    if (net.isServerRequestError(error)) {
      notifications.notifyError(error);
      getStore().dispatch({
        type: BulkAssemblyActionType.BULK_ASSEMBLY_VALIDATION_FAILED,
        assemblyId,
        message: error.message,
        responseJSON: error,
      });
    } else {
      throw error;
    }
  }
}

/**
 * Single action to update the entire state at once.
 */
export interface BulkAssemblyStateUpdated {
  type: BulkAssemblyActionType.BULK_ASSEMBLY_STATE_UPDATED;
  assemblyId: string;
  procedure: BulkAssemblyProcedure;
}

export interface BulkAssemblyValidationStarted {
  type: BulkAssemblyActionType.BULK_ASSEMBLY_VALIDATION_STARTED;
  assemblyId: string;
  input: BulkAssemblyProcedure;
}

export interface BulkAssemblyValidationFailed {
  type: BulkAssemblyActionType.BULK_ASSEMBLY_VALIDATION_FAILED;
  assemblyId: string;
  message: string;
  responseJSON: GenericResponseJSON;
}

export interface BulkAssemblyValidationCompleted {
  type: BulkAssemblyActionType.BULK_ASSEMBLY_VALIDATION_COMPLETED;
  assemblyId: string;
  data: AsyncBulkAssemblyValidationResults;
}

export interface BulkAssemblyFragmentStatusFilterUpdated {
  type: BulkAssemblyActionType.BULK_ASSEMBLY_FRAGMENT_STATUS_FILTER_UPDATED;
  assemblyId: string;
  statusFilter: ValidationStatus | null;
}

export interface BulkAssemblyConstructStatusFilterUpdated {
  type: BulkAssemblyActionType.BULK_ASSEMBLY_CONSTRUCT_STATUS_FILTER_UPDATED;
  assemblyId: string;
  statusFilter: ValidationStatus | null;
}

export type BulkAssemblyAction =
  | BulkAssemblyInitializedAction
  | BulkAssemblyStateUpdated
  | BulkAssemblyValidationStarted
  | BulkAssemblyValidationFailed
  | BulkAssemblyValidationCompleted
  | BulkAssemblyFragmentStatusFilterUpdated
  | BulkAssemblyConstructStatusFilterUpdated;
